import { Divider, Skeleton } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom-v5-compat";

import { BTButton, BTText } from "@btnet/core-components";

import { BdsIconX, BdsLayout } from "@buildertrend/components";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { getLoginTypeFromPortal, PortalType } from "utilities/portal/portal";

import { getCommentIdFromSearchParams } from "commonComponents/entity/comment/CommentComponent.utils";
import { CommentContainer } from "commonComponents/entity/comment/CommentContainer/CommentContainer";
import { InlinePreviewCommentHandler } from "commonComponents/entity/comment/CommentContainer/CommentContainer.api.handler";
import CommentTabs from "commonComponents/entity/comment/CommentContainer/CommentTabs";
import { DiscussionsHandler } from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.handler";
import {
    type DiscussionsEntity,
    type IHasDiscussionContainer,
    type ParticipantsResponse,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { PortalInfoContext } from "commonComponents/utilities/InlineOwnerPreview/PortalInfoContext";

import "./CommentComponent.less";

interface ICommentComponentProps {
    entity?: IHasDiscussionContainer;
    onUpdateCommentData?: (comments: DiscussionsEntity) => Promise<void> | void;
    setCommentDrawerOpen?: (isOpen: boolean) => void;
    handler?: DiscussionsHandler;
    hideHeader?: boolean;
    isReadonly?: boolean;
    isLoading?: boolean;
    onBeforeAddComment?: (currentLinkedId: number) => Promise<number | null>;
    defaultTabVisibility?: { canShowOwner?: boolean; canShowSubs?: boolean };
}

const previewHandler = () => {
    return new InlinePreviewCommentHandler();
};

const discussionsHandler = new DiscussionsHandler();

const CommentComponent = ({
    onUpdateCommentData,
    setCommentDrawerOpen,
    entity,
    handler = discussionsHandler,
    hideHeader = false,
    isReadonly,
    isLoading = false,
    onBeforeAddComment,
    defaultTabVisibility,
}: ICommentComponentProps) => {
    const [participants, setParticipants] = useState<ParticipantsResponse | undefined>(undefined);
    const { id, builderId } = entity || {};

    const portalInfo = useRequiredContext(PortalInfoContext);
    const [searchParams] = useSearchParams();

    const discussionId = useMemo(() => getCommentIdFromSearchParams(searchParams), [searchParams]);

    const loading = useRef(false);

    const getParticipants = useCallback(
        async (reloadParticipants?: boolean) => {
            // Early return if entity is not available
            if (!entity) {
                return;
            }

            // We've already done the api call, use the cached result
            if (
                (participants !== undefined && !reloadParticipants) ||
                entity.discussions.linkedDiscussionEntities.commentLinkedId <= 0
            ) {
                return;
            }

            try {
                const linkedId = entity.discussions.linkedDiscussionEntities.commentLinkedId;
                const discussionLinkedType =
                    entity.discussions.linkedDiscussionEntities.commentLinkedType;

                const newParticipants = await handler.participants(linkedId, discussionLinkedType);

                setParticipants(newParticipants);
            } catch (e) {
                showAPIErrorMessage(e);
            }
        },
        [entity, participants, handler],
    );

    const entityRef = useRef(entity);

    // reloading participants after the entity has been saved
    useEffect(() => {
        const loadParticipants = async () => {
            await getParticipants(true);
        };

        if (entity !== entityRef.current) {
            void loadParticipants();
            entityRef.current = entity;
        }
    }, [entity, getParticipants]);

    useEffect(() => {
        const loadParticipants = async () => {
            try {
                await getParticipants();
            } catch (e) {
                showAPIErrorMessage(e);
            } finally {
                loading.current = false;
            }
        };

        if (participants === undefined && !loading.current) {
            loading.current = true;
            void loadParticipants();
        } else if (participants) {
            loading.current = false;
        }
    }, [participants, getParticipants]);

    const reloadData = async () => {
        if (!entity || !builderId || !id) {
            return;
        }

        const discussionLinkedType = entity.discussions.linkedDiscussionEntities.commentLinkedType;

        try {
            const apiResponse = await handler.get(builderId, discussionLinkedType, id);
            if (entity.jobId) {
                apiResponse.jobId = entity.jobId;
            }
            const prevDiscussions = entity.discussions;

            const updatedDiscussions = apiResponse;
            if (prevDiscussions) {
                if (updatedDiscussions.addNewCommentPermissions !== undefined) {
                    updatedDiscussions.addNewCommentPermissions =
                        prevDiscussions.addNewCommentPermissions;
                }
                if (updatedDiscussions.comments.notificationUsers !== undefined) {
                    updatedDiscussions.comments.notificationUsers =
                        prevDiscussions.comments.notificationUsers;
                }
            }

            await onUpdateCommentData?.(updatedDiscussions);
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    const updateParentDiscussionState = async (newDiscussions: DiscussionsEntity) => {
        await onUpdateCommentData?.(newDiscussions);
    };

    const closeCommentDrawer = () => {
        setCommentDrawerOpen?.(false);
    };

    // Show skeleton when loading
    if (isLoading) {
        return (
            <div className="comment-skeleton-container">
                {/* Header */}
                <div className="flex flex-row justify-between padding-top-xs padding-horizontal-md padding-bottom-xs items-center">
                    <div>
                        <BTText size="distinct-lg" text="Comments" />
                    </div>
                    <div>
                        <BTButton
                            aria-label="Close comments"
                            type="tertiary"
                            icon={<BdsIconX />}
                            id="comment-drawer-close-button-skeleton"
                            onClick={closeCommentDrawer}
                            data-testid="comment-drawer-close-button-skeleton"
                        />
                    </div>
                </div>
                <Divider style={{ margin: "0" }} />

                {/* Content with padding */}
                <div className="comment-skeleton-content">
                    {/* Spacer to push content to bottom */}
                    <div className="comment-skeleton-spacer" />

                    {/* Skeleton for existing comment items */}
                    <div>
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="margin-bottom-lg">
                                <Skeleton
                                    avatar
                                    active
                                    paragraph={{ rows: 2 }}
                                    title={{ width: "40%" }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Divider */}
                    <Divider className="comment-skeleton-divider" />

                    {/* Skeleton for add comment form (editor + button) */}
                    <div className="comment-skeleton-add-comment">
                        <Skeleton.Input active className="comment-skeleton-input" />
                        <div className="comment-skeleton-button-wrapper">
                            <Skeleton.Button active className="comment-skeleton-button" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!entity) {
        return null;
    }

    return (
        <BdsLayout
            id="comment-drawer"
            testid="comment-drawer"
            useMaxWidth
            className="CommentTabsWrapper HasInvertedLayout"
        >
            {!hideHeader && (
                <div className="flex flex-row justify-between padding-top-xs padding-horizontal-md padding-bottom-xs items-center">
                    <div>
                        <BTText size="distinct-lg" text="Comments" />
                    </div>
                    <div>
                        {setCommentDrawerOpen !== undefined && (
                            <BTButton
                                type="tertiary"
                                icon={<BdsIconX />}
                                aria-label="Close comment drawer"
                                id="comment-drawer-close-button"
                                onClick={closeCommentDrawer}
                                data-testid="comment-drawer-close-button"
                            />
                        )}
                    </div>
                </div>
            )}
            <CommentTabs
                entity={entity}
                tabsVisible={[
                    portalInfo.currentPortal,
                    ...(portalInfo.currentPortal === PortalType.BUILDER
                        ? [PortalType.SUBS, PortalType.OWNER]
                        : []),
                ].map(getLoginTypeFromPortal)}
                updateParentDiscussionState={updateParentDiscussionState}
                discussionId={discussionId}
                defaultTabVisibility={defaultTabVisibility}
            >
                {(
                    tabbedEntity,
                    systemMessage,
                    emptyState,
                    filterParticipants,
                    updateState,
                    activeTab,
                ) => (
                    <CommentContainer
                        discussions={tabbedEntity.discussions}
                        participants={
                            filterParticipants ? filterParticipants(participants) : participants
                        }
                        linkedId={tabbedEntity.id}
                        reloadData={reloadData}
                        updateParentDiscussionState={updateState ?? (async () => {})}
                        useLegacyStyling={false}
                        handler={
                            portalInfo.currentPortal !== portalInfo.rootPortal
                                ? previewHandler()
                                : undefined
                        }
                        systemMessage={systemMessage}
                        emptyState={emptyState}
                        activeTab={activeTab}
                        scrollToDiscussionId={discussionId ?? 0}
                        isReadOnly={isReadonly}
                        onBeforeAddComment={onBeforeAddComment}
                    />
                )}
            </CommentTabs>
        </BdsLayout>
    );
};

export default CommentComponent;
